import { PATH_KEYS } from "@/services/appServices";
import { useItemService } from "./useServices";
import {
  DocumentLink,
  PostTemplateRequestBody,
} from "@/models/document-link/DocumentLink";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useItemsCrud } from "./useItemsCrud";
import { restError } from "@/models/client/RestResponse";

const useDocumentLink = () => {
  /**** Services ****/
  const documentLinkService = useItemService(
    (services) => services.documentLink
  );
  const queryClient = useQueryClient();
  const documentLinkCrud = useItemsCrud<DocumentLink>(
    [PATH_KEYS.documentLink],
    (services) => services.documentLink
  );

  /**** Queries & Mutations ****/
  const postTemplate = useMutation<string, unknown, PostTemplateRequestBody>({
    mutationKey: [PATH_KEYS.documentLink, "postTemplate"],
    mutationFn: async (requestBody: PostTemplateRequestBody) => {
      const response = await documentLinkService.postTemplate(requestBody);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.documentLink] });
    },
  });

  const useQueryByDocumentId = (
    documentId: string,
    options?: { enabled?: boolean }
  ) =>
    useQuery<DocumentLink[], Error, DocumentLink[], string[]>({
      queryKey: [PATH_KEYS.documentLink, "byDocumentId", documentId],
      queryFn: async () => {
        const response =
          await documentLinkService.fetchByDocumentId(documentId);
        if (response.success) return response.value;
        else throw restError(response);
      },
      placeholderData: undefined, // Prevent showing stale data from previous documentId
      enabled: options?.enabled !== false && !!documentId,
    });

  return {
    ...documentLinkCrud,
    postTemplate,
    useQueryByDocumentId,
  };
};

export default useDocumentLink;
